import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { SuggestedEditPart, UUID } from '@aline/common';
import { Apollo } from 'src/gql/generated';
import { useLogAndDispatchErrorSnack } from 'src/helpers/snackbars';
import { useResolveAllPlaybookSuggestedEdits, useResolvePlaybookSuggestedEdit } from 'src/hooks';

import { publishPlaybookFocus } from './playbook-focus-store';
import {
  buildSuggestionModel,
  findStop,
  getNextStopIndex,
  getPreviousStopIndex,
  resolveSelection,
  selectionFromRuleId,
  selectionFromStop,
} from './suggestion-model';
import type {
  PlaybookSelection,
  PlaybookSuggestionModel,
  SuggestionStop,
} from './suggestion-model';
import type { PlaybookDetail } from '../types';

/** How the URL follows a selection change: a click pushes, everything else replaces. */
export type RuleNavigationMode = 'push' | 'replace';

export interface SuggestionReview {
  /** The move (by entity id) whose arrow is drawn: hovered, else the current stop's. */
  activeMoveId: UUID | null;
  /** The current stop's place in the list, or null when no stop is current. */
  currentIndex: number | null;
  currentStop: SuggestionStop | null;
  goToNext: () => void;
  goToPrevious: () => void;
  /** Make a stop current without resolving it. */
  goToStop: (stop: SuggestionStop) => void;
  loading: boolean;
  model: PlaybookSuggestionModel;
  /** A click on a rule row: opens the rule, and pushes a history entry. */
  openRule: (ruleId: UUID) => void;
  /** Discard the pending move on an entity, for a manual drag that overrides it. */
  rejectPendingMove: (entityId: UUID) => Promise<void>;
  resolvePart: (
    stop: SuggestionStop,
    decision: Apollo.PlaybookSuggestedEditDecision
  ) => Promise<void>;
  resolveScope: (
    scope: Apollo.PlaybookSuggestedEditTargetInput,
    decision: Apollo.PlaybookSuggestedEditDecision
  ) => Promise<void>;
  /** The rule open in the details panel, derived from the selection. */
  selectedRuleId: UUID | null;
  /** Open a rule as a side effect (add, delete, or the stepper): replaces the history entry. */
  selectRule: (ruleId: UUID | null) => void;
  setHoveredMoveId: (entityId: UUID | null) => void;
  total: number;
}

const SuggestionReviewContext = createContext<SuggestionReview | null>(null);

export function useSuggestionReview(): SuggestionReview {
  const review = useContext(SuggestionReviewContext);
  if (!review) throw new Error('useSuggestionReview must be used inside SuggestionReviewProvider.');
  return review;
}

/**
 * The page has one selection: a rule, or a stop. Which rule the details panel
 * shows and which row is highlighted both derive from it, so a stop inside a
 * rule opens that rule and a click on a row drops the stop. Selection is
 * navigation, not a gate: every suggestion's own ✕ / ✓ stay live whether or
 * not it is current.
 *
 * The URL carries the open rule and follows the selection; it only drives the
 * selection on load and when the browser's back or forward button changes it.
 *
 * A stop selection follows its stop by key while suggestions stream in around
 * it, and falls back to the stop's last position once it is decided, so
 * resolving the current stop lands on the one that took its place, which is
 * how a review pass moves down the page.
 */
export function useSuggestionReviewState({
  onRuleChange,
  playbook,
  urlRuleId,
}: {
  onRuleChange: (ruleId: UUID | null, mode: RuleNavigationMode) => void;
  playbook: PlaybookDetail;
  /** The rule in the URL, read by the page from its route params. */
  urlRuleId: UUID | null;
}): SuggestionReview {
  const model = useMemo(() => buildSuggestionModel(playbook), [playbook]);
  const { stops } = model;
  const total = stops.length;
  const [selection, setSelection] = useState<PlaybookSelection>(() =>
    selectionFromRuleId(urlRuleId)
  );
  const { currentIndex, currentStop, selectedRuleId } = resolveSelection(model, selection);
  /**
   * The rule this hook last put in the URL. A URL change that matches it is
   * our own navigation landing; any other change came from outside (back,
   * forward, a link) and replaces the selection.
   */
  const lastNavigatedRuleIdRef = useRef<UUID | null>(urlRuleId);
  const [hoveredMoveId, setHoveredMoveIdState] = useState<UUID | null>(null);
  /**
   * Deciding a suggestion shifts the rows, and whatever lands under the still
   * pointer gets a hover it never asked for. Hover is ignored from a decision
   * until the pointer actually moves, so an arrow for some other move does
   * not spring up in the accepted one's place.
   */
  const [hoverSuppressed, setHoverSuppressed] = useState(false);

  const navigateToRule = useCallback(
    (ruleId: UUID | null, mode: RuleNavigationMode) => {
      if (ruleId === lastNavigatedRuleIdRef.current) return;
      lastNavigatedRuleIdRef.current = ruleId;
      onRuleChange(ruleId, mode);
    },
    [onRuleChange]
  );

  useEffect(() => {
    if (urlRuleId === lastNavigatedRuleIdRef.current) return;
    lastNavigatedRuleIdRef.current = urlRuleId;
    setSelection(selectionFromRuleId(urlRuleId));
  }, [urlRuleId]);

  /**
   * Keep the remembered position at the current stop as the list shifts under
   * it, and adopt whatever stop the fallback landed on, moving the URL with
   * it. Once no stop is left the selection becomes the rule that was open.
   * A no-op when nothing moved, so re-running on a tab switch changes nothing.
   */
  useEffect(() => {
    if (selection.type !== 'stop') return;
    if (!currentStop) {
      setSelection(selectionFromRuleId(selection.ruleId));
      return;
    }
    if (currentIndex === selection.index && currentStop.key === selection.key) return;
    setSelection(selectionFromStop(currentIndex ?? 0, currentStop));
    navigateToRule(currentStop.ruleId, 'replace');
  }, [currentIndex, currentStop, navigateToRule, selection]);

  /**
   * Tell the chat drawer what is open, so "this rule" or "this suggestion" in
   * a prompt means what is on screen. Re-publishing the same focus is a
   * no-op; the store is cleared when the page unmounts.
   */
  useEffect(() => {
    if (!selectedRuleId && !currentStop) {
      publishPlaybookFocus(null);
      return;
    }
    publishPlaybookFocus({
      focus: {
        ...(selectedRuleId ? { ruleId: selectedRuleId } : {}),
        ...(currentStop
          ? { stop: { part: partToInput(currentStop.part), target: currentStop.target } }
          : {}),
      },
      playbookId: playbook.id,
    });
  }, [currentStop, playbook.id, selectedRuleId]);
  useEffect(() => () => publishPlaybookFocus(null), []);

  useEffect(() => {
    if (!hoverSuppressed) return;
    function handlePointerMove(event: PointerEvent): void {
      /**
       * A layout change makes the browser replay the pointer in place; only a
       * real move, with a delta, lifts the suppression.
       */
      if (event.movementX === 0 && event.movementY === 0) return;
      setHoverSuppressed(false);
    }
    window.addEventListener('pointermove', handlePointerMove);
    return () => window.removeEventListener('pointermove', handlePointerMove);
  }, [hoverSuppressed]);

  const setHoveredMoveId = useCallback(
    (entityId: UUID | null) => {
      if (entityId !== null && hoverSuppressed) return;
      setHoveredMoveIdState(entityId);
    },
    [hoverSuppressed]
  );

  // A decision ends any hover and holds hover off until the pointer moves.
  const suppressHover = useCallback(() => {
    setHoveredMoveIdState(null);
    setHoverSuppressed(true);
  }, []);

  const { loading: resolvingPart, resolvePlaybookSuggestedEdit } =
    useResolvePlaybookSuggestedEdit();
  const { loading: resolvingScope, resolveAllPlaybookSuggestedEdits } =
    useResolveAllPlaybookSuggestedEdits();
  const dispatchErrorSnack = useLogAndDispatchErrorSnack();

  const select = useCallback(
    (next: PlaybookSelection, mode: RuleNavigationMode) => {
      setSelection(next);
      navigateToRule(resolveSelection(model, next).selectedRuleId, mode);
    },
    [model, navigateToRule]
  );

  const goToIndex = useCallback(
    (index: number | null) => {
      const stop = index === null ? null : stops.at(index);
      if (index === null || !stop) return;
      select(selectionFromStop(index, stop), 'replace');
    },
    [select, stops]
  );
  const goToNext = useCallback(
    () => goToIndex(getNextStopIndex(model, selection)),
    [goToIndex, model, selection]
  );
  const goToPrevious = useCallback(
    () => goToIndex(getPreviousStopIndex(model, selection)),
    [goToIndex, model, selection]
  );
  const goToStop = useCallback(
    (stop: SuggestionStop) => goToIndex(stops.findIndex(candidate => candidate.key === stop.key)),
    [goToIndex, stops]
  );

  const openRule = useCallback(
    (ruleId: UUID) => {
      // Reopening the open rule is a no-op rather than a duplicate history entry.
      if (selection.type === 'rule' && selection.ruleId === ruleId) return;
      select(selectionFromRuleId(ruleId), 'push');
    },
    [select, selection]
  );
  const selectRule = useCallback(
    (ruleId: UUID | null) => select(selectionFromRuleId(ruleId), 'replace'),
    [select]
  );

  const resolvePart = useCallback(
    async (stop: SuggestionStop, decision: Apollo.PlaybookSuggestedEditDecision) => {
      suppressHover();
      try {
        await resolvePlaybookSuggestedEdit({
          decision,
          part: partToInput(stop.part),
          target: stop.target,
        });
      } catch (error) {
        if (error instanceof Error) dispatchErrorSnack(error);
      }
    },
    [dispatchErrorSnack, resolvePlaybookSuggestedEdit, suppressHover]
  );

  const resolveScope = useCallback(
    async (
      scope: Apollo.PlaybookSuggestedEditTargetInput,
      decision: Apollo.PlaybookSuggestedEditDecision
    ) => {
      suppressHover();
      try {
        await resolveAllPlaybookSuggestedEdits({ decision, scope });
      } catch (error) {
        if (error instanceof Error) dispatchErrorSnack(error);
      }
    },
    [dispatchErrorSnack, resolveAllPlaybookSuggestedEdits, suppressHover]
  );

  const rejectPendingMove = useCallback(
    async (entityId: UUID) => {
      const stop = findStop(model, entityId, 'move');
      if (stop) await resolvePart(stop, Apollo.PlaybookSuggestedEditDecision.Reject);
    },
    [model, resolvePart]
  );

  /**
   * A hovered move counts only while it is still pending. Resolving the move
   * removes its pill from under the pointer, so no mouse-leave ever clears the
   * hover; without this check the arrow would stay up, drawn to rows that have
   * since moved.
   */
  const hoveredPendingMoveId =
    hoveredMoveId && findStop(model, hoveredMoveId, 'move') ? hoveredMoveId : null;
  const activeMoveId =
    hoveredPendingMoveId ?? (currentStop?.part === 'move' ? currentStop.entityId : null);

  return useMemo(
    () => ({
      activeMoveId,
      currentIndex,
      currentStop,
      goToNext,
      goToPrevious,
      goToStop,
      loading: resolvingPart || resolvingScope,
      model,
      openRule,
      rejectPendingMove,
      resolvePart,
      resolveScope,
      selectRule,
      selectedRuleId,
      setHoveredMoveId,
      total,
    }),
    [
      activeMoveId,
      currentIndex,
      currentStop,
      goToNext,
      goToPrevious,
      goToStop,
      model,
      openRule,
      rejectPendingMove,
      resolvePart,
      resolveScope,
      resolvingPart,
      resolvingScope,
      selectedRuleId,
      selectRule,
      setHoveredMoveId,
      total,
    ]
  );
}

export const SuggestionReviewProvider = React.memo(function SuggestionReviewProvider({
  children,
  review,
}: {
  children: React.ReactNode;
  review: SuggestionReview;
}): JSX.Element {
  return (
    <SuggestionReviewContext.Provider value={review}>{children}</SuggestionReviewContext.Provider>
  );
});

/**
 * The accept, reject and select handlers for one stop, stable while the stop
 * is. A null stop gives handlers that do nothing, so a caller whose stop comes
 * and goes can still call this unconditionally.
 */
export function useStopDecision(stop: SuggestionStop | null): {
  onAccept: () => void;
  onReject: () => void;
  onSelect: () => void;
} {
  const { goToStop, resolvePart } = useSuggestionReview();
  const onAccept = useCallback(() => {
    if (stop) void resolvePart(stop, Apollo.PlaybookSuggestedEditDecision.Accept);
  }, [resolvePart, stop]);
  const onReject = useCallback(() => {
    if (stop) void resolvePart(stop, Apollo.PlaybookSuggestedEditDecision.Reject);
  }, [resolvePart, stop]);
  const onSelect = useCallback(() => {
    if (stop) goToStop(stop);
  }, [goToStop, stop]);

  return useMemo(() => ({ onAccept, onReject, onSelect }), [onAccept, onReject, onSelect]);
}

/** Mouse handlers that draw a pending move's arrow while either of its ends is hovered. */
export function useMoveHover(entityId: UUID): {
  onMouseEnter: () => void;
  onMouseLeave: () => void;
} {
  const { setHoveredMoveId } = useSuggestionReview();
  const onMouseEnter = useCallback(() => setHoveredMoveId(entityId), [entityId, setHoveredMoveId]);
  const onMouseLeave = useCallback(() => setHoveredMoveId(null), [setHoveredMoveId]);

  return useMemo(() => ({ onMouseEnter, onMouseLeave }), [onMouseEnter, onMouseLeave]);
}

function partToInput(part: SuggestedEditPart): Apollo.PlaybookSuggestedEditPart {
  const byPart: Record<SuggestedEditPart, Apollo.PlaybookSuggestedEditPart> = {
    add: Apollo.PlaybookSuggestedEditPart.Add,
    delete: Apollo.PlaybookSuggestedEditPart.Delete,
    move: Apollo.PlaybookSuggestedEditPart.Move,
    patch: Apollo.PlaybookSuggestedEditPart.Patch,
  };
  return byPart[part];
}
